import { useCallback, useEffect } from 'react'
import AuthRoutes from '@/features/auth'
import EmailVerificationPage from '@/features/auth/pages/verify-email'
import useI18n from '@/shared/hooks/usei18n'
import { SessionContext } from '@/shared/hooks/useSession'
import App from './App'
import * as Sentry from '@sentry/react'
import { useGetUserSession, useLogin } from '@/features/auth/hooks/auth'
import { LoadingScreen } from '@/shared/components'
import OrganizationDeactivatedPage from '@/features/auth/pages/organization-deactivated'
import MagicLinkLoginErrorPage from '@/features/auth/pages/magic-link'
import { Route, Routes } from 'react-router-dom'
import type { Session } from '@/api/models/UserModel'

function MainRouteTree({
  isAuthenticated,
  session,
  onSignupSuccess,
  isLoggingIn
}: {
  isAuthenticated: boolean
  session: Session | null | undefined
  onSignupSuccess: (payload: { email: string; password: string }) => void | Promise<void>
  isLoggingIn: boolean
}) {
  if (!isAuthenticated || !session) {
    return <AuthRoutes onSignupSuccess={onSignupSuccess} isLoggingIn={isLoggingIn} />
  }

  return (
    <SessionContext.Provider value={{ session }}>
      <AuthenticatedAppContent session={session} />
    </SessionContext.Provider>
  )
}

function AuthenticatedAppContent({ session }: { session: Session }) {
  if (!session.isEmailVerified) {
    return <EmailVerificationPage />
  }
  if (
    session.organization.deactivatedAt !== null &&
    session.isPrivileged !== true
  ) {
    return <OrganizationDeactivatedPage />
  }
  return <App />
}

export default function Root() {
  const { handleChangeLanguage } = useI18n()
  const { mutateAsync: login, isPending: isLoggingIn } = useLogin()
  const {
    data: session,
    refetch,
    isFetched: isAuthCheckComplete,
    isSuccess: isSessionValid
  } = useGetUserSession()
  const isAuthenticated = isSessionValid && session != null

  const fetchSession = useCallback(async () => {
    try {
      const newSession = await refetch()
      if (!newSession.data) {
        return
      }
      handleChangeLanguage(newSession.data.language)
      Sentry.setUser({
        id: newSession.data.id,
        isEmailVerified: newSession.data.isEmailVerified,
        organizationId: newSession.data.organization.id
      })
    } catch (error) {
      console.error(error)
    }
  }, [handleChangeLanguage, refetch])

  useEffect(() => {
    if (!isAuthCheckComplete) {
      fetchSession()
    }
  }, [fetchSession, isAuthCheckComplete])

  const onSignupSuccess = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      await login({ email, password })
      await fetchSession()
    },
    [login, fetchSession]
  )

  if (!isAuthCheckComplete) {
    return <LoadingScreen />
  }

  return (
    <Routes>
      <Route
        path="magic-link"
        element={<MagicLinkLoginErrorPage isAuthenticated={isAuthenticated} />}
      />
      <Route
        path="*"
        element={
          <MainRouteTree
            isAuthenticated={isAuthenticated}
            session={session}
            onSignupSuccess={onSignupSuccess}
            isLoggingIn={isLoggingIn}
          />
        }
      />
    </Routes>
  )
}
